import { useCallback, useMemo, useState } from "react";
import { useSdk } from "../../contexts/services/hooks/use-services-context";
import { INITIAL_ANALYSIS_IN_PROGRESS_KEY } from "../../managers/mail/queries";
import type { FocusSortMode } from "../../managers/user";
import { useSessionQuery } from "../session/useSessionQuery";
import { useUserProfile } from "./useUserProfile";

export type FocusSortModeControl = {
    /** Mode lu dans le profil ; importance tant que le profil ne le porte pas. */
    mode: FocusSortMode;
    setMode: (mode: FocusSortMode) => Promise<void>;
    /** Faux pendant l'analyse de démarrage (onboarding, mails reçus depuis la dernière connexion) et pendant une bascule. */
    isAvailable: boolean;
};

/**
 * Mode de tri du Focus. Le mode demandé s'affiche pendant la bascule ; ensuite le mode affiché est celui
 * du profil du SDK : l'ancien si l'enregistrement a échoué, le nouveau s'il n'a manqué que le rechargement du Focus.
 */
export function useFocusSortMode(): FocusSortModeControl {
    const sdk = useSdk();
    const { data: userProfile } = useUserProfile();
    const { data: isInitialAnalysisInProgress } = useSessionQuery<boolean>(
        INITIAL_ANALYSIS_IN_PROGRESS_KEY,
    );
    const [pendingMode, setPendingMode] = useState<FocusSortMode | null>(null);

    const setMode = useCallback(
        async (mode: FocusSortMode) => {
            setPendingMode(mode);
            try {
                await sdk.userManager.setFocusSortMode(mode);
            } finally {
                setPendingMode(null);
            }
        },
        [sdk],
    );

    return useMemo(
        () => ({
            mode:
                pendingMode ??
                userProfile?.settings.displayPreferences.focusSortMode ??
                "IMPORTANCE",
            setMode,
            isAvailable:
                isInitialAnalysisInProgress === false && pendingMode === null,
        }),
        [userProfile, isInitialAnalysisInProgress, pendingMode, setMode],
    );
}
